/**
 * AudioPlayer - Handles audio playback with queue system
 */
import EventEmitter from './EventEmitter.js';

export default class AudioPlayer extends EventEmitter {
  constructor(config) {
    super();
    this.config = config;
    this.audioContext = null;
    this.audioQueue = [];
    this.isPlaying = false;
    this.isProcessingQueue = false;
    this.currentSource = null;
    this.audioContextReady = false;
    
    // Pre-initialize AudioContext to prevent first chunk cut-off (non-blocking)
    // This will create the AudioContext early, but won't block if it fails
    this.initializeAudioContext().catch(() => {
      // Silently handle initialization errors - will be initialized lazily on first audio
    });
  }
  
  /**
   * Initialize AudioContext early to prevent first chunk cut-off
   */
  async initializeAudioContext() {
    if (this.audioContext && this.audioContext.state !== 'closed') {
      return;
    }
    
    try {
      // Use configured sample rate if available, otherwise default to 24 kHz
      // Prefer sampleRate, fall back to outputSampleRate, then default to match typical TTS output
      const sampleRate = this.config.sampleRate || this.config.outputSampleRate || 24000;
      const contextOptions = { sampleRate: sampleRate };
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)(contextOptions);
      
      // Resume AudioContext if suspended (browsers require user interaction)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      
      // Ensure AudioContext is ready by creating a silent buffer and playing it briefly
      // This "warms up" the AudioContext to prevent first chunk cut-off
      if (this.audioContext.state === 'running') {
        try {
          const sampleRate = this.audioContext.sampleRate;
          const buffer = this.audioContext.createBuffer(1, sampleRate * 0.01, sampleRate); // 10ms silent buffer
          const source = this.audioContext.createBufferSource();
          source.buffer = buffer;
          source.connect(this.audioContext.destination);
          source.start(0);
          source.stop(this.audioContext.currentTime + 0.01);
        } catch (warmupError) {
          // Ignore warmup errors - AudioContext is still usable
          console.debug('AudioPlayer: AudioContext warmup skipped:', warmupError);
        }
      }
      
      this.audioContextReady = true;
    } catch (error) {
      console.warn('AudioPlayer: Failed to pre-initialize AudioContext:', error);
      // Will be initialized lazily on first audio chunk
      this.audioContextReady = false;
    }
  }
  
  /**
   * Add audio data to playback queue
   */
  playAudio(audioData) {
    try {
      const audioBlob = this.createAudioBlob(audioData);
      this.audioQueue.push(audioBlob);
      
      // Process queue if not already playing or processing
      // Also check if we have a current source - if not, we should start processing
      if (!this.isPlaying && !this.isProcessingQueue && !this.currentSource) {
        // If AudioContext is already ready, process immediately (no delay)
        // Otherwise, use a small delay to allow initialization
        const delay = this.audioContextReady ? 0 : 50;
        setTimeout(() => this.processQueue(), delay);
      }
    } catch (error) {
      this.emit('playbackError', error);
    }
  }
  
  /**
   * Create audio blob from ArrayBuffer
   */
  createAudioBlob(arrayBuffer) {
    const uint8Array = new Uint8Array(arrayBuffer);
    
    // Detect audio format
    if (uint8Array.length >= 4) {
      // WAV header (RIFF)
      if (uint8Array[0] === 0x52 && uint8Array[1] === 0x49 && 
          uint8Array[2] === 0x46 && uint8Array[3] === 0x46) {
        return new Blob([arrayBuffer], { type: 'audio/wav' });
      }
      
      // MP3 header
      if (uint8Array[0] === 0xFF && (uint8Array[1] & 0xE0) === 0xE0) {
        return new Blob([arrayBuffer], { type: 'audio/mpeg' });
      }
      
      // OGG header
      if (uint8Array[0] === 0x4F && uint8Array[1] === 0x67 && 
          uint8Array[2] === 0x67 && uint8Array[3] === 0x53) {
        return new Blob([arrayBuffer], { type: 'audio/ogg' });
      }
    }
    
    // Default to WAV format
    return new Blob([arrayBuffer], { type: 'audio/wav' });
  }
  
  /**
   * Process audio queue
   */
  async processQueue() {
    // Prevent multiple simultaneous queue processing
    // Note: isPlaying check removed to allow seamless queue processing
    if (this.isProcessingQueue || this.audioQueue.length === 0) {
      return;
    }
    
    this.isProcessingQueue = true;
    
    const audioBlob = this.audioQueue.shift();
    if (!audioBlob) {
      this.isProcessingQueue = false;
      return;
    }
    
    try {
      // Check if we were playing BEFORE async operations (decode, etc.)
      // This ensures we detect the true state before any async delays
      const wasNotPlayingBefore = !this.isPlaying && this.currentSource === null;
      
      // Ensure AudioContext exists and is ready
      if (!this.audioContext || this.audioContext.state === 'closed') {
        await this.initializeAudioContext();
      }
      
      const audioContext = this.audioContext;
      
      // Resume AudioContext if suspended
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
        // Small delay after resume to ensure AudioContext is fully ready
        // This prevents the first chunk from being cut off
        if (wasNotPlayingBefore) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }
      
      // Ensure AudioContext is in running state before proceeding
      // This is critical to prevent first chunk cut-off
      if (audioContext.state !== 'running') {
        console.warn('AudioPlayer: AudioContext not in running state:', audioContext.state);
        // Try to resume again
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }
      
      // Create audio source from blob
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Double-check state AFTER async operations - audio might have been stopped
      // If currentSource is still null and isPlaying is still false, we're truly starting
      const isTrulyStarting = wasNotPlayingBefore && !this.isPlaying && this.currentSource === null;
      
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);
      
      // Set state BEFORE emitting event to prevent race conditions
      this.currentSource = source;
      this.isPlaying = true;
      
      // Emit playbackStarted right before actually starting playback
      // This ensures the event is sent when audio actually starts playing
      // CRITICAL: This must be emitted when audio truly starts, not before
      if (isTrulyStarting) {
        this.emit('playbackStarted');
      }
      
      // Handle audio end
      source.onended = () => {
        this.currentSource = null;
        this.isProcessingQueue = false;
        
        // Process next audio in queue if there are more items
        if (this.audioQueue.length > 0) {
          // More audio to play - continue processing without emitting playbackStopped
          // Keep isPlaying = true since we'll continue playing
          // Use immediate processing for gapless playback (0ms instead of 50ms)
          setTimeout(() => this.processQueue(), 0);
        } else {
          // Queue is empty - playback has truly ended
          // Small delay to ensure smooth transition if new audio arrives quickly
          setTimeout(() => {
            // Check again if new audio arrived during the delay
            if (this.audioQueue.length === 0 && !this.currentSource) {
              this.isPlaying = false;
              this.emit('playbackStopped');
            }
          }, 100);
        }
      };
      
      // Start playback - AudioContext should now be fully ready
      source.start();
      
    } catch (error) {
      this.currentSource = null;
      this.emit('playbackError', error);
      
      // Try to process next audio in queue if there are more items
      if (this.audioQueue.length > 0) {
        this.isProcessingQueue = false;
        setTimeout(() => this.processQueue(), 100);
      } else {
        // Queue is empty - playback has ended
        this.isPlaying = false;
        this.isProcessingQueue = false;
        this.emit('playbackStopped');
      }
    }
  }
  
  /**
   * Stop current playback and clear queue
   */
  stop() {
    this.stopImmediate();
  }
  
  /**
   * Stop current playback immediately and clear queue
   */
  stopImmediate() {
    const wasPlaying = this.isPlaying || this.currentSource !== null;
    
    if (this.currentSource) {
      try {
        this.currentSource.stop();
      } catch (error) {
        // Ignore errors when stopping
      }
      this.currentSource = null;
    }
    
    this.isPlaying = false;
    this.isProcessingQueue = false;
    this.audioQueue = [];
    
    // Only emit playbackStopped if audio was actually playing
    if (wasPlaying) {
      this.emit('playbackStopped');
    }
  }
  
  /**
   * Resume AudioContext if suspended
   * Critical for mobile: getUserMedia can suspend playback AudioContext
   */
  async resumeAudioContext() {
    if (!this.audioContext || this.audioContext.state === 'closed') {
      // If AudioContext doesn't exist, initialize it
      await this.initializeAudioContext();
      return;
    }
    
    if (this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
        console.log('✅ AudioPlayer: AudioContext resumed after mic permission');
      } catch (error) {
        console.warn('⚠️ AudioPlayer: Failed to resume AudioContext:', error);
      }
    }
  }
  
  /**
   * Get playback status
   */
  getStatus() {
    return {
      isPlaying: this.isPlaying,
      isProcessingQueue: this.isProcessingQueue,
      queueLength: this.audioQueue.length,
      audioContextState: this.audioContext ? this.audioContext.state : 'closed'
    };
  }
  
  /**
   * Cleanup resources
   */
  destroy() {
    this.stop();
    
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.audioContext = null;
    }
    
    this.audioContextReady = false;
  }
}
